"use client"

import { LocationId, LOCATIONS, PHONE_CONSULTATION_URL } from "@/data/booking-appointments"
import { LocationCard } from "../ui/LocationCard"
import { Phone } from "lucide-react"
import { trackEvent } from "@/lib/analytics"

interface LocationStepProps {
  onSelect: (location: LocationId) => void
}

/**
 * LocationStep - Choose Noosa, Maroochydore, or Phone Consultation (first step)
 *
 * User selects their preferred clinic first, then proceeds to
 * select service type (pillar), appointment type, and confirm.
 *
 * Phone Consultation is a special case - it opens Jane directly.
 */
export function LocationStep({ onSelect }: LocationStepProps) {
  const locations: LocationId[] = ["noosa", "maroochydore"]

  const handlePhoneConsultation = () => {
    trackEvent("booking_phone_consultation_click")
    window.open(PHONE_CONSULTATION_URL, "_blank")
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-xl font-normal text-[#3f4141] mb-2 mt-10">
          Choose your clinic
        </h2>
        <p className="text-sm font-light text-[#3f4141] mt-1">
          Select a location to begin
        </p>
      </div>

      {/* Clinic Location Options */}
      <div className="space-y-4">
        {locations.map((locationId) => (
          <LocationCard
            key={locationId}
            locationId={locationId}
            locationName={LOCATIONS[locationId]}
            onSelect={() => onSelect(locationId)}
          />
        ))}
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200 pt-4" />

      {/* Phone Consultation Section */}
      <div className="space-y-3">
        <div className="text-center">
          <h3 className="text-lg font-normal text-[#3f4141] mb-2">
            Not able to attend in person?
          </h3>
          <p className="text-sm font-light text-[#3f4141]">
            You're welcome to book an online or phone consultation with Renee.
          </p>
        </div>

        <button
          onClick={handlePhoneConsultation}
          className="w-full p-4 text-left bg-gray-50 border border-gray-200 rounded-xl hover:border-[#848974] hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-[#848974] flex-shrink-0" />
              <h3 className="text-base font-normal text-[#3f4141]">
                Online / Phone Consultation
              </h3>
            </div>
            <div className="text-[#848974] font-medium text-sm">
              Select
            </div>
          </div>
        </button>
      </div>
    </div>
  )
}
